import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout        from "../../../layout/MessengerLayout";
import PayrollFilters         from "./filters/PayrollFilters";
import PayrollKpis            from "./kpi/PayrollKpis";
import RunActionsBar          from "./run/RunActionsBar";
import EmployeePayrollGrid    from "./grid/EmployeePayrollGrid";
import EmployeePayDrawer      from "./drawer/EmployeePayDrawer";
import ExceptionsPanel        from "./exceptions/ExceptionsPanel";
import RunSummary             from "./summary/RunSummary";
import TrendChart             from "./reports/TrendChart";
import CostByDept             from "./reports/CostByDept";
import ExportPanel            from "./exports/ExportPanel";
import ComingSoonOverlay      from "../ComingSoonOverlay";
import CtcCalculator          from "./ctc/CtcCalculator";
import SalaryStructureTab     from "./tabs/SalaryStructureTab";
import StatutoryTab           from "./tabs/StatutoryTab";
import ReimbursementsTab      from "./tabs/ReimbursementsTab";
import LoansTab               from "./tabs/LoansTab";
import BankDisbursementTab    from "./tabs/BankDisbursementTab";
import PayrollReportsTab      from "./tabs/PayrollReportsTab";
import PayrollSettingsTab     from "./tabs/PayrollSettingsTab";
import PayrollSetupTab        from "./tabs/PayrollSetupTab";
import { toast }              from "../../ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const TABS = [
  { key: "overview",    label: "Overview"       },
  { key: "setup",       label: "Payroll Setup"  },
  { key: "structure",   label: "Salary Structure"},
  { key: "statutory",   label: "Statutory"      },
  { key: "reimburse",   label: "Reimbursements" },
  { key: "loans",       label: "Loans"          },
  { key: "bank",        label: "Bank & Disburse" },
  { key: "reports",     label: "Reports"        },
  { key: "ctc",         label: "CTC Calculator" },
  { key: "settings",    label: "Settings"       },
];

// Phase 4a admin-sweep — tabs whose CONCEPT is India-specific (CTC salary
// structure, PF/ESI/Form-16 statutory, the India CTC calculator). Hidden
// for non-India orgs; India is unchanged. Display gating only — no calc.
const INDIA_ONLY_TABS = new Set(["structure", "statutory", "ctc"]);

// Phase 4b — the mirror of the above. Payroll Setup captures HMRC PAYE /
// Accounts Office references and registers the org as a Staffology (UK)
// employer, so it is meaningless-to-wrong for an India org. Shown only
// once the region resolves to UK — never while loading or for unknown.
const UK_ONLY_TABS = new Set(["setup"]);

export default function AdminPayrollPage() {
  const [tab,     setTab]     = useState("overview");
  const [month,   setMonth]   = useState(new Date().toISOString().slice(0, 7));
  const [filters, setFilters] = useState({ dept: "ALL", location: "ALL", q: "" });

  const [kpis,       setKpis]       = useState(null);
  const [summary,    setSummary]    = useState(null);
  const [rows,       setRows]       = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [runStatus,  setRunStatus]  = useState("DRAFT");

  // Phase 4a admin-sweep — the org's payroll region ("UK"|"India"|"unknown"),
  // used to gate India-only tabs/labels. null while loading. Fetched once.
  const [region, setRegion] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/admin/payroll/region`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setRegion(d?.payroll_region || "unknown"); })
      .catch(() => { if (alive) setRegion("unknown"); });
    return () => { alive = false; };
  }, []);

  // Until region resolves, and for any non-India region, hide India-only
  // tabs (never leak India UI to a UK/unknown org). India → all tabs
  // except the UK-only ones.
  const visibleTabs = TABS.filter(t => {
    if (INDIA_ONLY_TABS.has(t.key)) return region === "India";
    if (UK_ONLY_TABS.has(t.key))    return region === "UK";
    return true;
  });

  // If the selected tab isn't visible for this region, fall back to Overview.
  useEffect(() => {
    if (!visibleTabs.some(t => t.key === tab)) setTab("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  useEffect(() => {
    if (tab !== "overview") return;
    let alive = true;

    const run = () => {
      setLoading(true); setError(null);
      Promise.all([
        fetch(`${API}/api/admin/payroll/kpis?month=${month}`,                                                                                                  { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/payroll/run/summary?month=${month}`,                                                                                           { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/payroll/employees?month=${month}&dept=${filters.dept}&location=${filters.location}&q=${filters.q}`,                           { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/payroll/exceptions?month=${month}`,                                                                                            { headers: authHeaders() }).then(r => r.json()),
      ])
        .then(([k, s, e, x]) => {
          if (!alive) return;
          setKpis(k || null);
          setSummary(s || null);
          setRunStatus(k?.runStatus || s?.status || "DRAFT");
          setRows(Array.isArray(e) ? e : []);
          setExceptions(Array.isArray(x) ? x : []);
        })
        .catch(e => { if (alive) setError(e.message); })
        .finally(() => alive && setLoading(false));
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["payslip_correction", "income_certificate", "bank_update"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      alive = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [tab, month, filters]);

  const totals = useMemo(() => ({
    gross:      rows.reduce((s, r) => s + (r.earnings   || 0), 0),
    net:        rows.reduce((s, r) => s + (r.net        || 0), 0),
    deductions: rows.reduce((s, r) => s + (r.deductions || 0), 0),
    headcount:  rows.length,
  }), [rows]);

  // Labels per action — also used by RunActionsBar via the prop callbacks
  // when the bar shows its own toast feedback.
  const ACTION_LABELS = {
    approve:             "approve",
    lock:                "lock",
    recalc:              "recalculate",
    "generate-payslips": "generate payslips",
    disburse:            "disburse",
    reopen:              "reopen",
  };

  // Returns a promise so the caller (RunActionsBar) can chain its own
  // info/success/error toasts. Recalc / Approve / Lock / Generate are
  // toasted by RunActionsBar; Disburse / Reopen are toasted here.
  const runAction = async (path) => {
    const label = ACTION_LABELS[path] || path;
    const res = await fetch(`${API}/api/admin/payroll/run/${path}?month=${month}`, { method: "POST", headers: authHeaders() });
    if (!res.ok) {
      setError(`HTTP ${res.status}`);
      throw new Error(`HTTP ${res.status}`);
    }
    const STATUS_MAP = { approve: "REVIEW", lock: "LOCKED", disburse: "PAID", reopen: "DRAFT", recalc: runStatus };
    setRunStatus(STATUS_MAP[path] || runStatus);
    return label;
  };

  const runActionWithToast = async (path) => {
    const label = ACTION_LABELS[path] || path;
    try {
      await runAction(path);
      toast.success(`Payroll ${label} successful`);
    } catch (_) {
      toast.error(`Failed to ${label}`);
    }
  };

  const fixException = async (userId, code) => {
    try {
      const res = await fetch(`${API}/api/admin/payroll/exceptions/${userId}/fix?code=${code}&month=${month}`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setExceptions(prev => prev.map(r => ({ ...r, exceptions: (r.exceptions || []).filter(e => !(r.id === userId && e === code)) })));
      toast.success("Exception resolved");
    } catch {
      toast.error("Failed to resolve exception");
    }
  };

  const holdToggle = async (userId, held) => {
    try {
      const res = await fetch(`${API}/api/admin/payroll/employee/${userId}/hold?month=${month}`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ onHold: held }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(prev => prev.map(r => r.id === userId ? { ...r, onHold: held } : r));
      toast.success("Employee hold updated");
    } catch {
      toast.error("Failed to update hold");
    }
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Payroll</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage runs, salary structures, statutory compliance and disbursement</p>
        </div>

        {/* Phase 4a admin-sweep — region banner (India = no banner). */}
        {region === "UK" && (
          <div className="rounded-xl border border-[#00ff90]/20 bg-[#00ff90]/[0.04] p-3 text-sm text-white/60">
            <span className="text-[#00ff90] font-medium">UK payroll (PAYE / NI).</span>{" "}
            India-only tabs (CTC Calculator, Statutory, Salary Structure) are hidden for this org.
            Per-employee UK statutory config lives in each employee's Payroll Details; full UK pay
            runs via Staffology arrive in a later phase.
          </div>
        )}
        {region === "unknown" && (
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3 text-sm text-white/60">
            <span className="text-amber-400 font-medium">Payroll region not set for this org.</span>{" "}
            Showing a neutral view — India-specific tabs are hidden. Set the org's payroll
            country/region to enable region-specific payroll.
          </div>
        )}

        {/* Tab nav */}
        <div className="flex gap-1 flex-wrap border-b border-white/5 pb-1 overflow-x-auto">
          {visibleTabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key ? "border-[#00ff90] text-[#00ff90]" : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === "overview" && (
          <>
            <PayrollFilters month={month} setMonth={setMonth} filters={filters} setFilters={setFilters} />
            <RunActionsBar
              status={runStatus}
              loading={loading}
              onApprove={() => runAction("approve")}
              onLock={() => runAction("lock")}
              onRecalc={() => runAction("recalc")}
              onGenerate={() => runAction("generate-payslips")}
              onDisburse={() => runActionWithToast("disburse")}
              onReopen={() => runActionWithToast("reopen")}
            />
            <PayrollKpis loading={loading} kpis={kpis} />
            <RunSummary totals={totals} variance={summary?.variance} />
            {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}
            <EmployeePayrollGrid rows={rows} onOpen={setSelected} onHoldToggle={holdToggle} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ExceptionsPanel rows={exceptions} onFix={fixException} />
              <TrendChart month={month} />
              <CostByDept month={month} />
            </div>
            <ComingSoonOverlay note="Payroll exports — coming soon">
              <ExportPanel month={month} totals={totals} />
            </ComingSoonOverlay>
          </>
        )}

        {/* Phase 4b — a real, live feature: no ComingSoonOverlay. */}
        {tab === "setup"      && <PayrollSetupTab />}

        {tab === "structure"  && (
          <ComingSoonOverlay note="Salary structure / components — coming soon">
            <SalaryStructureTab  />
          </ComingSoonOverlay>
        )}
        {tab === "statutory"  && (
          <ComingSoonOverlay note="Statutory PF / ESI / generate filings — coming soon">
            <StatutoryTab   month={month} />
          </ComingSoonOverlay>
        )}
        {tab === "reimburse"  && (
          <ComingSoonOverlay note="Reimbursements module — coming soon">
            <ReimbursementsTab month={month} />
          </ComingSoonOverlay>
        )}
        {tab === "loans"      && (
          <ComingSoonOverlay note="Employee loans module — coming soon">
            <LoansTab />
          </ComingSoonOverlay>
        )}
        {tab === "bank"       && (
          <ComingSoonOverlay note="Bank disbursement (NACH / BACS) — coming soon">
            <BankDisbursementTab month={month} runStatus={runStatus} />
          </ComingSoonOverlay>
        )}
        {tab === "reports"    && <PayrollReportsTab />}
        {tab === "ctc"        && (
          <div className="rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] p-5">
            <CtcCalculator />
          </div>
        )}
        {tab === "settings"   && <PayrollSettingsTab />}

      </div>

      <EmployeePayDrawer
        open={!!selected}
        row={selected}
        region={region || "unknown"}
        onClose={() => setSelected(null)}
        onSaveAnnualSalary={(userId, payload) =>
          fetch(`${API}/api/admin/payroll/employee/${userId}/ctc`, {
            method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
          })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); })
            .then(() => { toast.success("Salary updated"); setSelected(null); })
            .catch(() => toast.error("Failed to update salary"))
        }
        onAddAdjustment={(userId, adj) =>
          fetch(`${API}/api/admin/payroll/employee/${userId}/adjustments?month=${month}`, {
            method: "POST", headers: authHeaders(), body: JSON.stringify(adj),
          })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); })
            .then(() => { toast.success("Adjustment added"); setSelected(null); })
            .catch(() => toast.error("Failed to add adjustment"))
        }
      />
    </MessengerLayout>
  );
}